import { useRef } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import type { DismissalItem } from "../lib/api.js";
import { DismissButton } from "./DismissButton.js";

/**
 * Horizontal-scrolling row of anime cards from AniList.
 *
 * Anime items don't live in our media_items table — they're metadata only.
 * Clicking a tile launches the native Crunchyroll app on the current device
 * with the title pre-searched (when an intent URL applies), or opens the
 * Crunchyroll web search as a fallback.
 */

export interface AnimePick {
  id: string;
  title: string;
  romaji_title: string | null;
  english_title: string | null;
  thumbnail_url: string | null;
  backdrop_url: string | null;
  overview: string | null;
  rating: number | null;
  genres: string[];
  year: number | null;
  episodes: number | null;
  duration_min: number | null;
  status: string | null;
  format: string | null;
  anilist_url: string;
  crunchyroll_search_url: string;
}

type Platform = "wallaceTv" | "androidPhone" | "desktop";

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/WallaceTV\//.test(ua)) return "wallaceTv";
  if (/Android/i.test(ua)) return "androidPhone";
  return "desktop";
}

function launchCrunchyroll(pick: AnimePick): void {
  const platform = detectPlatform();
  const title = pick.english_title ?? pick.romaji_title ?? pick.title;
  if (platform === "wallaceTv") {
    // Launch Crunchyroll's native ATV app. The app handles its own
    // home/search; passing the title via extra is best-effort.
    window.location.href = `intent://search?q=${encodeURIComponent(title)}#Intent;package=com.crunchyroll.crunchyroid;scheme=https;end`;
    return;
  }
  if (platform === "androidPhone") {
    window.location.href = `intent://www.crunchyroll.com/search?q=${encodeURIComponent(title)}#Intent;package=com.crunchyroll.crunchyroid;scheme=https;end`;
    return;
  }
  window.open(pick.crunchyroll_search_url, "_blank", "noopener");
}

export function AnimeStrip({
  label,
  caption,
  items,
  onRefresh,
  onDismiss,
  dismissing = false,
}: {
  label: string;
  caption?: string;
  items: AnimePick[];
  onRefresh?: () => void;
  onDismiss?: (item: DismissalItem) => void;
  dismissing?: boolean;
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  function scrollBy(dir: -1 | 1): void {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <div className="section-label">{label}</div>
          {caption && <div className="text-parchment/60 text-xs">{caption}</div>}
        </div>
        <div className="flex gap-1">
          <button className="pill-ghost p-2" onClick={() => scrollBy(-1)} aria-label="scroll left">
            <ChevronLeft size={16} />
          </button>
          <button className="pill-ghost p-2" onClick={() => scrollBy(1)} aria-label="scroll right">
            <ChevronRight size={16} />
          </button>
          {onRefresh && (
            <button className="pill-ghost p-2 text-parchment/50 hover:text-gold" onClick={onRefresh} aria-label="refresh">
              <RefreshCw size={14} />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((it) => (
          <AnimeCard key={it.id} pick={it} onDismiss={onDismiss} dismissing={dismissing} />
        ))}
      </div>
    </section>
  );
}

function AnimeCard({ pick, onDismiss, dismissing = false }: {
  pick: AnimePick;
  onDismiss?: (item: DismissalItem) => void;
  dismissing?: boolean;
}): JSX.Element {
  const rating = pick.rating !== null ? Math.round(pick.rating * 10) / 10 : null;
  return (
    <div className="min-w-[180px] snap-start relative group">
      <button
        onClick={() => launchCrunchyroll(pick)}
        className="w-full card group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi hover:scale-[1.04] transition-transform duration-200 ease-spring text-left"
        title={pick.overview ?? pick.title}
      >
      <div className="aspect-[2/3] bg-elev relative">
        {pick.thumbnail_url ? (
          <img
            src={pick.thumbnail_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-2xl">
            {pick.title.slice(0, 2).toUpperCase()}
          </div>
        )}
        <span
          className="absolute top-2 right-11 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display"
          style={{ background: "rgba(15,6,6,0.78)", color: "#F47521", border: "1px solid #F4752155" }}
        >
          Anime
        </span>
        {rating !== null && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/85 border border-gold/40 font-display tabular-nums text-goldHi" title={`AniList rating ${rating}/10`}>
            ★ {rating}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">{pick.title}</div>
        <div className="text-parchment text-xs mt-1 truncate">
          {pick.genres.slice(0, 2).join(" · ") || pick.format || "Anime"}
        </div>
      </div>
      </button>
      {onDismiss && (
        <DismissButton
          itemTitle={pick.title}
          onDismiss={() => onDismiss({ ...pick, kind: "anime" })}
          disabled={dismissing}
        />
      )}
    </div>
  );
}
